import {
  Button,
  Column,
  Copy,
  Row,
  Squish,
  SvgX,
  SvgPencil,
} from '@grubhubprod/gh-cookbook-react';
import React from 'react';
import { useParams } from 'react-router-dom';
import { NoResultsMsg } from 'src/components/Table/NoResultsMsg';
import Table from 'src/components/Table/Table';
import {
  IGroup,
  ISearchParams,
  SearchStatus,
} from 'src/utilities/useSearchHooks';
import { SimpleDialog } from 'src/components/SimpleDialog/SimpleDialog';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import {
  SortableColumn,
  SortFields,
  SortOrder,
} from 'src/components/Table/SortableColumn';
import { RouterButtonLink } from 'src/components/RouterLink/RouterButtonLink';
import { useRemoveGroup } from './hooks/useRemoveGroup';

export interface IGroupTableProps {
  searchStatus: SearchStatus;
  totalResults: number;
  hasMoreResults: boolean;
  groups: IGroup[];
  hasNoResultsWithoutQuery: boolean;
  hasNoResultsWithQuery: boolean;
  currentQueryText: string;
  loadMoreGroupsSize: number;
  bodyHeight?: string;
  showFooter?: boolean;
  getCorpGroups: (
    params?: ISearchParams,
    isLoadingMore?: boolean
  ) => Promise<void>;
  previousSortOrder?: SortOrder;
  previousSortField?: SortFields;
  onSortColumnClick?: (field: SortFields, order: SortOrder) => void;
}

export function GroupTable({
  searchStatus,
  totalResults,
  hasMoreResults,
  groups,
  hasNoResultsWithoutQuery,
  hasNoResultsWithQuery,
  currentQueryText,
  loadMoreGroupsSize: loadMoreGroupsNumber,
  bodyHeight,
  showFooter = false,
  getCorpGroups,
  previousSortOrder,
  previousSortField,
  onSortColumnClick,
}: IGroupTableProps): JSX.Element {
  const { isDesktop } = useScreenSizes();
  const { corpid: corpId } = useParams<{ corpid: string }>();

  const handleRemoveGroupSuccess = () => {
    const groupSearchParams: ISearchParams = { page: 1 };
    getCorpGroups(groupSearchParams);
  };

  const {
    removeGroupModalStatus,
    groupIdDeletionInProgress,
    removeGroup,
    onRemoveGroup,
    closeRemoveGroupModal,
  } = useRemoveGroup({
    corpId,
    onSuccess: handleRemoveGroupSuccess,
  });

  function loadMoreGroups() {
    const groupSearchParams: ISearchParams = {};

    getCorpGroups(groupSearchParams, true);
  }

  return (
    <>
      <Table
        hideHeader={!isDesktop}
        header={() => (
          <Row>
            <Column phone={10}>
              <Row>
                <Column phone={4}>
                  <SortableColumn
                    label="Name"
                    previousSortOrder={previousSortOrder}
                    sortField={SortFields.NAME}
                    previousSortField={previousSortField}
                    onSortColumnClick={onSortColumnClick}
                  />
                </Column>
                <Column phone={5}>
                  <Copy variant="body" color="secondaryText">
                    Description
                  </Copy>
                </Column>
                <Column phone={3}>
                  <Copy variant="body" color="secondaryText">
                    No. of employees
                  </Copy>
                </Column>
              </Row>
            </Column>
            <Column phone={2}>
              <Copy variant="body" color="secondaryText">
                Actions
              </Copy>
            </Column>
          </Row>
        )}
        body={() =>
          totalResults > 0 ? (
            <ul className="removeListStyles">
              {groups.map((group) => (
                <Squish
                  as="li"
                  inset="md"
                  key={group.id}
                  borderBottom="1px solid"
                  borderColor="lightLine"
                >
                  <Row alignItems="center" justifyContent="space-between">
                    <Column phone={10}>
                      <Row>
                        <Column desktop={4} className="ellipsisOverflow">
                          {group.name}
                        </Column>
                        <Column desktop={5} className="ellipsisOverflow">
                          {group.description}
                        </Column>
                        <Column desktop={3} className="ellipsisOverflow">
                          {isDesktop ? (
                            group.diner_count
                          ) : (
                            <Copy variant="caption" color="secondaryText">
                              {`${group.diner_count} ${
                                group.diner_count === 1 ? 'person' : 'people'
                              }`}
                            </Copy>
                          )}
                        </Column>
                      </Row>
                    </Column>

                    <Column phone={2}>
                      <Row>
                        <Column phone={6}>
                          <RouterButtonLink
                            href={`/corporate/management/${corpId}/groups/${group.id}`}
                            buttonSize="medium"
                            buttonVariant="tertiary"
                            useBlockButton
                            className="tableActionButtons"
                          >
                            {isDesktop ? (
                              'Manage'
                            ) : (
                              <SvgPencil size="24px" aria-label="Manage" />
                            )}
                          </RouterButtonLink>
                        </Column>
                        <Column phone={6}>
                          <Button
                            variant="tertiary"
                            buttonSize="medium"
                            className="tableActionButtons"
                            onClick={() => onRemoveGroup(group)}
                          >
                            {isDesktop ? (
                              'Remove'
                            ) : (
                              <SvgX size="24px" aria-label="Remove" />
                            )}
                          </Button>
                        </Column>
                      </Row>
                    </Column>
                  </Row>
                </Squish>
              ))}
              {hasMoreResults && (
                <Squish inset="lg">
                  <Button
                    variant="secondary"
                    isLoading={searchStatus === SearchStatus.LOADING}
                    block
                    onClick={loadMoreGroups}
                  >
                    {searchStatus === SearchStatus.COMPLETE && (
                      <span>Load {loadMoreGroupsNumber} more</span>
                    )}
                    {searchStatus === SearchStatus.ERROR && (
                      <>
                        <Copy variant="body" fontFamily="default">
                          Something went wrong loading your results.{' '}
                          <Copy variant="body" color="interactive">
                            Try again
                          </Copy>
                        </Copy>
                      </>
                    )}
                  </Button>
                </Squish>
              )}
            </ul>
          ) : (
            <>
              {hasNoResultsWithoutQuery && (
                <NoResultsMsg message="Add groups to assign lines of credit" />
              )}
              {hasNoResultsWithQuery && (
                <NoResultsMsg
                  message={`No results matching '${currentQueryText}'`}
                />
              )}
            </>
          )
        }
        showFooter={showFooter}
        {...(isDesktop && {
          bodyMinHeight: bodyHeight,
          bodyMaxHeight: bodyHeight,
        })}
      />
      {removeGroupModalStatus.showModal && (
        <SimpleDialog
          id={removeGroupModalStatus.groupId}
          primaryActionFlavor="danger"
          title={`Remove ${removeGroupModalStatus.groupName} group?`}
          description="Any lines of credit given to all people in this group will no longer be available."
          primaryActionText="Remove"
          isPrimaryButtonLoading={groupIdDeletionInProgress !== null}
          buttonsDirection="row-reverse"
          secondaryActionText="Cancel"
          disablePrimaryActionButton={groupIdDeletionInProgress !== null}
          disableSecondaryActionButton={groupIdDeletionInProgress !== null}
          onSecondaryActionClick={closeRemoveGroupModal}
          onPrimaryActionClick={(groupId) => removeGroup(groupId)}
        />
      )}
    </>
  );
}
